import React, { useEffect, useRef, useState } from 'react';
import { API_BASE } from '../config/api';

const EditarPedidoDocente = ({ pedido, onClose, onActualizar }) => {
  const [tipo, setTipo] = useState('');
  const [descripcion, setDescripcion] = useState('');

  const [colegios, setColegios] = useState([]);
  const [salas, setSalas] = useState([]);
  const [equipos, setEquipos] = useState([]);

  const [colegioId, setColegioId] = useState('');
  const [salaId, setSalaId] = useState('');
  const [equipoId, setEquipoId] = useState('');

  const [cargandoColegios, setCargandoColegios] = useState(false);
  const [cargandoSalas, setCargandoSalas] = useState(false);
  const [cargandoEquipos, setCargandoEquipos] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const profesorId = localStorage.getItem('profesorId');

  // ✅ Evitar “pisadas” si el pedido cambia rápido
  const pedidoIdRef = useRef(null);

  useEffect(() => {
    if (!pedido) return;

    pedidoIdRef.current = pedido.id;

    // ✅ Mapear tipo desde BD -> UI
    const t =
      pedido.tipo_pedido === 'Soporte Técnico'
        ? 'soporte'
        : pedido.tipo_pedido === 'Soporte de Sala'
        ? 'soporte_sala'
        : pedido.tipo_pedido === 'Insumos'
        ? 'insumo'
        : pedido.tipo_pedido === 'Mobiliario'
        ? 'mobiliario'
        : pedido.tipo_pedido === 'Kit de Robótica'
        ? 'kit_robotica'
        : '';

    setTipo(t);
    setDescripcion(pedido.descripcion_problema || '');

    // ✅ IMPORTANTÍSIMO: forzar strings para que el <select> seleccione bien
    setColegioId(pedido.colegio_id != null ? String(pedido.colegio_id) : '');
    setSalaId(pedido.sala_id != null ? String(pedido.sala_id) : '');
    setEquipoId(pedido.equipo_id != null ? String(pedido.equipo_id) : '');

    const cargarColegios = async () => {
      if (!profesorId) return;

      setCargandoColegios(true);
      try {
        const res = await fetch(`${API_BASE}/api/profesores/${profesorId}/colegios`);
        if (!res.ok) throw new Error('No se pudo cargar colegios');
        const data = await res.json();
        // ✅ si cambió el pedido mientras cargaba, no actualizar
        if (pedidoIdRef.current !== pedido.id) return;
        setColegios(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error cargando colegios:', error);
        setColegios([]);
      } finally {
        setCargandoColegios(false);
      }
    };

    cargarColegios();
  }, [pedido, profesorId]);

  // Cargar salas cuando cambia colegioId
  useEffect(() => {
    const cargarSalas = async () => {
      if (!colegioId) {
        setSalas([]);
        return;
      }

      setCargandoSalas(true);
      try {
        const res = await fetch(`${API_BASE}/api/colegios/${colegioId}/salas`);
        if (!res.ok) throw new Error('No se pudo cargar salas');
        const data = await res.json();
        setSalas(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error cargando salas:', error);
        setSalas([]);
      } finally {
        setCargandoSalas(false);
      }
    };

    cargarSalas();
  }, [colegioId]);

  // Cargar equipos cuando cambia salaId
  useEffect(() => {
    const cargarEquipos = async () => {
      if (!salaId) {
        setEquipos([]);
        return;
      }

      setCargandoEquipos(true);
      try {
        const res = await fetch(`${API_BASE}/api/salas/${salaId}/equipos`);
        if (!res.ok) throw new Error('No se pudo cargar equipos');
        const data = await res.json();
        setEquipos(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error cargando equipos:', error);
        setEquipos([]);
      } finally {
        setCargandoEquipos(false);
      }
    };

    cargarEquipos();
  }, [salaId]);

  // ✅ Si cambia el tipo y NO es soporte técnico, limpiar equipo (no se usa)
  useEffect(() => {
    if (tipo !== 'soporte') setEquipoId('');
  }, [tipo]);

  const handleGuardar = async () => {
    // Mapear UI -> BD
    let tipoFinal = '';
    if (tipo === 'soporte') tipoFinal = 'Soporte Técnico';
    else if (tipo === 'soporte_sala') tipoFinal = 'Soporte de Sala';
    else if (tipo === 'insumo') tipoFinal = 'Insumos';
    else if (tipo === 'mobiliario') tipoFinal = 'Mobiliario';
    else if (tipo === 'kit_robotica') tipoFinal = 'Kit de Robótica';

    if (!tipoFinal) {
      alert('Seleccioná el tipo de pedido.');
      return;
    }
    if (!colegioId) {
      alert('Seleccioná el colegio.');
      return;
    }
    if (!salaId) {
      alert('Seleccioná la sala.');
      return;
    }

    // ✅ Validación: Soporte Técnico requiere equipo
    if (tipo === 'soporte' && !equipoId) {
      alert('Debes seleccionar un equipo para Soporte Técnico.');
      return;
    }

    const cuerpo = {
      tipo_pedido: tipoFinal,
      descripcion_problema: descripcion,
      colegio_id: Number(colegioId),
      sala_id: Number(salaId),
      equipo_id: tipo === 'soporte' ? Number(equipoId) : null,
    };

    setGuardando(true);
    try {
      const respuesta = await fetch(`${API_BASE}/api/pedidos/${pedido.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cuerpo),
      });

      if (respuesta.ok) {
        alert('Pedido actualizado correctamente');
        onActualizar(); // esto también cierra el modal desde MisPedidos
      } else {
        let msg = 'Error al actualizar el pedido';
        try {
          const data = await respuesta.json();
          if (data?.mensaje) msg = data.mensaje;
        } catch {}
        alert(msg);
      }
    } catch (error) {
      console.error('Error al actualizar el pedido:', error);
      alert('Error de conexión al actualizar el pedido');
    } finally {
      setGuardando(false);
    }
  };

  // ✅ Handlers que limpian dependencias cuando el usuario cambia combos
  const onChangeColegio = (v) => {
    setColegioId(v);
    setSalaId('');
    setEquipoId('');
    setSalas([]);
    setEquipos([]);
  };

  const onChangeSala = (v) => {
    setSalaId(v);
    setEquipoId('');
    setEquipos([]);
  };

  return (
    // ✅ sin mt-6 para que no “se esconda” en modal en pantallas chicas
    <div className="bg-white p-4 md:p-6 rounded">
      <h3 className="text-lg font-bold mb-4 text-blue-700">Editar Pedido</h3>

      <select
        value={tipo}
        onChange={(e) => setTipo(e.target.value)}
        className="w-full border px-3 py-2 rounded mb-3"
        required
      >
        <option value="">-- Tipo de pedido --</option>
        <option value="soporte">Soporte Técnico</option>
        <option value="soporte_sala">Soporte de Sala</option>
        <option value="insumo">Insumos</option>
        <option value="mobiliario">Mobiliario</option>
        <option value="kit_robotica">Kit de Robótica</option>
      </select>

      <select
        value={colegioId}
        onChange={(e) => onChangeColegio(e.target.value)}
        className="w-full border px-3 py-2 rounded mb-3"
        required
        disabled={cargandoColegios}
      >
        <option value="">
          {cargandoColegios ? 'Cargando colegios...' : '-- Seleccionar colegio --'}
        </option>
        {colegios.map((c) => (
          <option key={c.id} value={String(c.id)}>
            {c.nombre}
          </option>
        ))}
      </select>

      <select
        value={salaId}
        onChange={(e) => onChangeSala(e.target.value)}
        className="w-full border px-3 py-2 rounded mb-3"
        required
        disabled={!colegioId || cargandoSalas}
      >
        <option value="">
          {!colegioId
            ? '-- Seleccionar sala --'
            : cargandoSalas
            ? 'Cargando salas...'
            : '-- Seleccionar sala --'}
        </option>
        {salas.map((s) => (
          <option key={s.id} value={String(s.id)}>
            {s.nombre}
          </option>
        ))}
      </select>

      {tipo === 'soporte' && (
        <select
          value={equipoId}
          onChange={(e) => setEquipoId(e.target.value)}
          className="w-full border px-3 py-2 rounded mb-3"
          required
          disabled={!salaId || cargandoEquipos}
        >
          <option value="">
            {!salaId
              ? '-- Seleccionar equipo --'
              : cargandoEquipos
              ? 'Cargando equipos...'
              : '-- Seleccionar equipo --'}
          </option>
          {equipos.map((eq) => (
            <option key={eq.id} value={String(eq.id)}>
              {eq.nombre}
            </option>
          ))}
        </select>
      )}

      <textarea
        placeholder="Descripción"
        value={descripcion}
        onChange={(e) => setDescripcion(e.target.value)}
        className="w-full border px-3 py-2 rounded mb-3"
        rows={3}
        required
      />

      <div className="flex gap-3 justify-end">
        <button
          onClick={onClose}
          disabled={guardando}
          className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition"
        >
          Cancelar
        </button>
        <button
          onClick={handleGuardar}
          disabled={guardando}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
        >
          {guardando ? 'Guardando...' : 'Guardar cambios'}
        </button>
      </div>
    </div>
  );
};

export default EditarPedidoDocente;
